'use client';

import { useEffect } from 'react';
import { useRouter, usePathname } from 'next/navigation';
import { NestedHome, Broker, NestedSearch, NestedProject } from '@habity/types';
import { getHomePath, getBrokerPath, getProjectPath } from '@habity/services';
import { parseIdFromPath } from '@habity/utils';

/**
 * Hook to update the URL with the complete slug if it's missing parts
 * This ensures that even if users access a profile via a partial URL (like just the ID),
 * they'll get the full, SEO-friendly URL with all slug components
 * 
 * Works with home, broker, and project profiles
 */
export const useUpdateSlug = (entity: NestedHome | Broker | NestedSearch | NestedProject | null | undefined) => {
  const router = useRouter();
  const pathname = usePathname();
  
  useEffect(() => {
    if (!entity || !pathname) return;
    
    // Extract the current slug from the pathname
    const pathParts = pathname.split('/');
    const currentSlug = pathParts[pathParts.length - 1];
    
    // Parse the entity ID from the current slug
    const entityId = parseIdFromPath(currentSlug);
    
    // If we can't parse the ID or it doesn't match the entity's ID, don't update
    if (!entityId || Number(entityId) !== entity.id) return;
    
    // Determine entity type and generate the complete path
    let completePath = '';
    
    // Check if it's a home (has address property)
    if ('address' in entity) {
      completePath = getHomePath(entity as NestedHome);
    } 
    // Check if it's a broker (has name property but no heading - to distinguish from project)
    else if ('name' in entity && !('heading' in entity)) {
      completePath = getBrokerPath(entity as Broker);
    }
    // Check if it's a project (has name and heading properties)
    else if ('name' in entity && 'heading' in entity) {
      completePath = getProjectPath(entity as NestedProject);
    }
    // If we can't determine the entity type, don't update
    else {
      return;
    }
    
    // Extract just the slug part from the complete path (strip query string first)
    const completePathOnly = completePath.split('?')[0];
    const completePathParts = completePathOnly.split('/');
    const completeSlug = completePathParts[completePathParts.length - 1];
    
    // If the current slug is different from the complete slug, update the URL
    // Use completePath directly — getHomePath already includes ?preview= when needed
    if (currentSlug !== completeSlug) {
      router.replace(completePath, { scroll: false });
    }
  }, [entity, pathname, router]);
};

// Export the old hook name for backward compatibility
export const useUpdateHomeSlug = useUpdateSlug;
